import log from 'loglevel';
// analytics/utils/utils.js - Minimal version for compatibility

// Enhanced browser fingerprinting
const generateFingerprint = async () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Browser fingerprint', 2, 2);
  
  // Enhanced platform detection
  let platformInfo = 'unknown';
  try {
    if (navigator.userAgentData) {
      const uaData = await navigator.userAgentData.getHighEntropyValues(['platform']);
      platformInfo = uaData.platform || navigator.userAgentData.platform;
    } else {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('windows')) platformInfo = 'Windows';
      else if (ua.includes('mac')) platformInfo = 'macOS';
      else if (ua.includes('linux')) platformInfo = 'Linux';
      else if (ua.includes('android')) platformInfo = 'Android';
      else if (ua.includes('iphone') || ua.includes('ipad')) platformInfo = 'iOS';
      else platformInfo = navigator.platform || 'unknown';
    }
  } catch (e) {
    platformInfo = navigator.platform || 'unavailable';
  }
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    platformInfo,
    navigator.cookieEnabled,
    canvas.toDataURL(),
    navigator.hardwareConcurrency || 0,
    navigator.deviceMemory || 0,
  ].join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

// Parse URL parameters
const parseUrlParams = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    
    return {
      campaign_id: params.get('campaign') || params.get('campaign_id') || null,
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term: params.get('utm_term') || null,
      utm_content: params.get('utm_content') || null,
      gclid: params.get('gclid') || null,
      fbclid: params.get('fbclid') || null,
      ref: params.get('ref') || null,
    };
  } catch (e) {
    log.warn('Analytics: Error parsing URL parameters', e);
    return {};
  }
};

// Helper function to get auth headers
const getAuthHeaders = (token) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }
  
  return headers;
};

// Get token from localStorage/sessionStorage (fallback method)
const getStoredToken = () => {
  try {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || null;
  } catch (e) {
    log.warn('Analytics: Error accessing token storage', e);
    return null;
  }
};

const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem('auth_token', token);
      log.log('Analytics: Token stored for API calls');
    } else {
      removeStoredToken();
    }
  } catch (e) {
    log.warn('Analytics: Error storing token', e);
  }
};

const removeStoredToken = () => {
  try {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    log.log('Analytics: Token removed from storage');
  } catch (e) {
    log.warn('Analytics: Error removing token', e);
  }
};

const syncTokenToStorage = (token) => {
  if (token) {
    setStoredToken(token);
  } else {
    removeStoredToken();
  }
};

// Check if user is likely a bot/crawler
const isLikelyBot = () => {
  const botPatterns = [
    /bot/i, /crawler/i, /spider/i, /scraper/i,
    /googlebot/i, /bingbot/i, /facebookbot/i,
    /twitterbot/i, /linkedinbot/i
  ];
  
  const userAgent = navigator.userAgent.toLowerCase();
  return botPatterns.some(pattern => pattern.test(userAgent));
};

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Throttle utility
const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export {
  generateFingerprint,
  parseUrlParams,
  getAuthHeaders,
  getStoredToken,
  setStoredToken,
  removeStoredToken,
  syncTokenToStorage,
  isLikelyBot,
  debounce,
  throttle,
};